import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuth } from '../../context/AuthContext';
import { urlAdjuntoTramite, useDetalleTramite, useGestionarTramite } from '../../hooks/useBandejaTramites';
import { ESTADOS_ABIERTOS, getEstadoTramiteColor, getEstadoTramiteLabel, type AccionTramite } from '../../types/tramiteGremio';
import { formatCuit, formatDate, formatDateTime } from '../../utils/format';

interface Props {
  id: string;
  /** En pantallas chicas se muestra solo el detalle y hace falta volver a la lista. */
  onVolver?: () => void;
}

const ORIGEN: Record<string, string> = { afiliado: 'El afiliado', gremio: 'El gremio', sistema: 'Sistema' };
const MIN_MENSAJE = 5;

function bytes(n: number | null): string {
  if (!n) return '';
  return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function TramiteGremioDetalle({ id, onVolver }: Props) {
  const { profile } = useAuth();
  const { data: t, isLoading, error } = useDetalleTramite(id);
  const gestionar = useGestionarTramite();

  const [mensaje, setMensaje] = useState('');
  const [errorForm, setErrorForm] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [errorAdjunto, setErrorAdjunto] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<AccionTramite | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !t) {
    return (
      <Stack spacing={2}>
        {onVolver && (
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={onVolver}>
              Volver a la lista
            </Button>
          </Box>
        )}
        <Alert severity="warning">{error ? error.message : 'No encontramos ese trámite.'}</Alert>
      </Stack>
    );
  }

  const abierto = ESTADOS_ABIERTOS.includes(t.estado);
  const sinAsignar = !t.asignado_a;
  const mio = !!profile && t.asignado_a === profile.id;

  const ejecutar = async (accion: AccionTramite) => {
    setErrorForm(null);
    setAviso(null);
    try {
      await gestionar.mutateAsync({ tramiteId: t.id, accion, mensaje });
      setMensaje('');
      setAviso(
        accion === 'tomar'
          ? 'Tomaste el trámite.'
          : accion === 'pedir_informacion'
            ? 'Le pediste información al afiliado.'
            : accion === 'resolver'
              ? 'Trámite resuelto.'
              : 'Trámite rechazado.',
      );
    } catch (e) {
      setErrorForm((e as Error).message);
    }
  };

  const handleAccion = (accion: AccionTramite) => {
    setErrorForm(null);
    setAviso(null);
    if (accion !== 'tomar' && mensaje.trim().length < MIN_MENSAJE) {
      setErrorForm(`Escribí un mensaje para el afiliado (mínimo ${MIN_MENSAJE} caracteres).`);
      return;
    }
    if (accion === 'resolver' || accion === 'rechazar') {
      setConfirmar(accion);
      return;
    }
    void ejecutar(accion);
  };

  const abrirAdjunto = async (path: string) => {
    setErrorAdjunto(null);
    try {
      window.open(await urlAdjuntoTramite(path), '_blank', 'noopener');
    } catch (e) {
      setErrorAdjunto((e as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      {onVolver && (
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={onVolver}>
            Volver a la lista
          </Button>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h2">
              {t.tipo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.codigo} · iniciado el {formatDate(t.created_at)}
            </Typography>
          </Box>
          <Chip label={getEstadoTramiteLabel(t.estado)} color={getEstadoTramiteColor(t.estado)} />
        </Stack>
        <Typography sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{t.detalle}</Typography>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Adjuntos
        </Typography>
        {t.adjuntos.length === 0 ? (
          <Typography color={t.requiere_adjunto ? 'error' : 'text.secondary'}>{t.requiere_adjunto ? 'Sin adjuntos (este tipo exige adjunto).' : 'Sin adjuntos'}</Typography>
        ) : (
          <Stack spacing={0.5}>
            {t.adjuntos.map((a) => (
              <Link key={a.id} component="button" type="button" underline="hover" onClick={() => abrirAdjunto(a.path)} sx={{ textAlign: 'left', display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                <DescriptionIcon fontSize="small" /> {a.nombre} {a.tamano ? `(${bytes(a.tamano)})` : ''}
              </Link>
            ))}
          </Stack>
        )}
        {errorAdjunto && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorAdjunto}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Afiliado
        </Typography>
        <Typography fontWeight={700}>
          {t.afiliado.apellido}, {t.afiliado.nombre}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          CUIL {formatCuit(t.afiliado.cuil)}
          {t.afiliado.telefono ? ` · ${t.afiliado.telefono}` : ''}
          {t.afiliado.email ? ` · ${t.afiliado.email}` : ''}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {t.empresas.map((e, i) => (
            <Chip key={i} size="small" variant="outlined" label={`${e.razon_social}${e.estado === 'activa' ? '' : ' (baja)'}`} />
          ))}
          {!t.afiliado.tiene_cuenta && <Chip size="small" color="warning" variant="outlined" label="Sin cuenta en el portal" />}
          {t.afiliado.estado !== 'activo' && <Chip size="small" color="error" variant="outlined" label={`Afiliado ${t.afiliado.estado}`} />}
        </Stack>
      </Paper>

      {abierto && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} spacing={1}>
            <Typography variant="subtitle2">
              {sinAsignar ? 'Sin asignar' : mio ? 'Asignado a vos' : `Asignado a ${t.asignado_nombre ?? 'otra persona'}`}
            </Typography>
            {sinAsignar && (
              <Button size="small" variant="outlined" onClick={() => handleAccion('tomar')} disabled={gestionar.isPending}>
                Tomar
              </Button>
            )}
          </Stack>
          <TextField
            label="Mensaje para el afiliado"
            helperText="El afiliado lo ve en el seguimiento de su trámite."
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 2000 }}
          />
          {errorForm && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorForm}
            </Alert>
          )}
          {aviso && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {aviso}
            </Alert>
          )}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
            <Button variant="outlined" color="warning" onClick={() => handleAccion('pedir_informacion')} disabled={gestionar.isPending}>
              Pedir información
            </Button>
            <Button variant="contained" color="success" onClick={() => handleAccion('resolver')} disabled={gestionar.isPending}>
              Resolver
            </Button>
            <Button variant="outlined" color="error" onClick={() => handleAccion('rechazar')} disabled={gestionar.isPending}>
              Rechazar
            </Button>
          </Stack>
        </Paper>
      )}
      {!abierto && aviso && <Alert severity="success">{aviso}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Seguimiento
        </Typography>
        <Stack divider={<Divider />} spacing={1.5}>
          {t.eventos.map((ev) => (
            <Box key={ev.id}>
              <Typography fontWeight={600} sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                {ev.mensaje}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ev.autor ?? ORIGEN[ev.origen] ?? ev.origen}
                {ev.autor ? ' (gremio)' : ''} · {formatDateTime(ev.created_at)}
                {ev.estado_nuevo ? ` · ${getEstadoTramiteLabel(ev.estado_nuevo)}` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Dialog open={!!confirmar} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmar === 'resolver' ? 'Resolver el trámite' : 'Rechazar el trámite'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            El trámite se cierra y {t.afiliado.nombre} va a ver tu mensaje. Un trámite cerrado no se puede reabrir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={confirmar === 'resolver' ? 'success' : 'error'}
            onClick={() => {
              const a = confirmar!;
              setConfirmar(null);
              void ejecutar(a);
            }}
          >
            {confirmar === 'resolver' ? 'Resolver' : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
